import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useLoading from "../hooks/useLoading";
import { getBookingByID, updateBookingStatus } from "../api/BookingAPIUtils";
import { Booking } from "../models/BookingModel";
import IconLocation from "../components/Icons/IconLocation";
import IconPeople from "../components/Icons/IconPeople";
import CustomDateTimePicker from "../components/CustomDateTimePicker/CustomDateTimePicker";
import { useAppSelector } from "../hooks/useAppSelector";

const BookingConfirmationPage: React.FC = () => {
  const userRole = useAppSelector((state) => state.user.role);
  const { startLoading, stopLoading } = useLoading();
  const { bookingId } = useParams<{ bookingId: string }>();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [error, setError] = useState<string | null>(null); // Error state
  const navigate = useNavigate();

  const handleApprove = useCallback(async () => {
    if (!booking) return;

    try {
      const response = await updateBookingStatus(booking.id, "confirm");
      if (response.success) {
        alert(`Booking approved successfully.`);
        navigate("/");
      } else {
        throw new Error(`Failed to approve booking: ${response.message}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    }
  }, [booking]);

  const handleDecline = useCallback(async () => {
    if (!booking) return;

    try {
      const response = await updateBookingStatus(booking.id, "cancel");
      if (response.success) {
        alert(`Booking declined successfully.`);
        navigate("/");
      } else {
        throw new Error(`Failed to decline booking: ${response.message}`);
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    }
  }, [booking]);

  useEffect(() => {
    if (booking === null) return;
    if (booking?.status === "confirmed") {
      alert("This booking has already been confirmed.");
      navigate("/");
    } else if (booking?.status === "cancelled") {
      alert("This booking has already been cancelled.");
      navigate("/");
    }
  }, [booking]);

  useEffect(() => {
    if (userRole !== "Admin" && userRole !== "Staff Manager") {
      alert("You do not have permission to access this page.");
      navigate("/");
    }
  }, [userRole]);

  const fetchBooking = useCallback(async () => {
    if (!bookingId) {
      setError("Booking ID is missing.");
      return;
    }
    try {
      startLoading();
      const response = await getBookingByID(bookingId);
      if (response.success) {
        setBooking(response.data);
        setError(null);
      } else {
        if (
          response.message == "Booking not found: sql: no rows in result set"
        ) {
          alert("Booking not found. Please check the booking ID.");
          navigate("/");
          return;
        }
        throw new Error(response.message || "Failed to fetch booking details.");
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred.");
      }
    } finally {
      stopLoading();
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  if (!booking) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <p className="text-gray-600">Loading booking details...</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-screen bg-gray-100">
      <div className="flex flex-col bg-white shadow-lg rounded-lg p-6 w-full h-full md:h-auto md:w-2/3 xl:w-1/2 justify-between lg:items-center lg:justify-center">
        <div className="flex flex-col lg:flex-row gap-6 lg:w-full">
          {/* Booking Details */}
          <div className="hidden lg:flex lg:w-auto">
            <CustomDateTimePicker
              startDate={new Date(booking.startTime)}
              endDate={new Date(booking.endTime)}
              onDateChange={() => {}} // No-op since it's read-only
              readOnly={true}
            />
          </div>
          <div className="flex flex-col space-y-4 lg:w-2/3 justify-between">
            <div>
              <h1 className="hidden lg:block text-xl font-bold text-center mb-2">
                Booking Confirmation
              </h1>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-semibold">
                    Resource: {booking.resourceName}
                  </h2>
                </div>
                <div className="flex flex-col space-y-2 ml-2">
                  <div className="flex items-center space-x-2">
                    <IconLocation width="18" height="18" />
                    <p className="text-sm text-gray-600">{booking.location}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <IconPeople width="18" height="18" />
                    <p className="text-sm text-gray-600">
                      {booking.fullname || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="flex flex-col space-y-1">
                  <span className="text-md font-semibold text-gray-700">
                    Reason:
                  </span>
                  <p className="text-sm text-gray-600 m-2 line-clamp-3 lg:line-clamp-6">
                    {booking.purpose}
                  </p>
                </div>
                <div className="block lg:hidden">
                  <CustomDateTimePicker
                    startDate={new Date(booking.startTime)}
                    endDate={new Date(booking.endTime)}
                    onDateChange={() => {}} // No-op since it's read-only
                    readOnly={true}
                  />
                </div>
              </div>
            </div>
            <div className="hidden lg:block">
              <ActionButtons
                onApprove={handleApprove}
                onDecline={handleDecline}
              />
            </div>
          </div>
        </div>
        <div className="block lg:hidden">
          <ActionButtons onApprove={handleApprove} onDecline={handleDecline} />
        </div>
      </div>
    </div>
  );
};

const ActionButtons: React.FC<{
  onApprove: () => void;
  onDecline: () => void;
}> = ({ onApprove, onDecline }) => {
  return (
    <div className="mt-6 flex justify-center space-x-4">
      <button
        onClick={onApprove}
        className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
      >
        Approve
      </button>
      <button
        onClick={onDecline}
        className="px-4 py-2 text-sm font-medium text-white bg-state-400 rounded-md hover:bg-state-400/90"
      >
        Decline
      </button>
    </div>
  );
};

export default BookingConfirmationPage;
